import React, { useEffect, useMemo, useState } from "react";
import MedicationSearch from "./MedicationSearch";
import PreviewBlocks from "./PrescriptorPreview";
import GroupedSelectionModal from "../../../pages/doctor/modals/GroupedSelectionModal";
import { createDosageLine } from "./prescriptorData";

const FieldIndicator = ({ error }) => {
  if (!error) return null;
  return (
    <span
      className={
        error.severity === "warning"
          ? "np-field-indicator warning"
          : "np-field-indicator error"
      }
      title={error.message}
      aria-label={error.message}
    >
      !
    </span>
  );
};

const FieldWrap = ({ error, children }) => (
  <div className="np-field-wrap">
    {children}
    <FieldIndicator error={error} />
  </div>
);

const PrescriptionForm = ({
  selectedBlock,
  blockTree,
  previewEntryNote,
  selectedBlockId,
  selectedLineId,
  onSelectPreviewLine,
  onSelectPreviewBlock,
  onDeletePreviewLine,
  onSaveEntry,
  onResetEntryForm,
  selectedEntry,
  onUpdateEntry,
  onShowJson,
  showAdvanced,
  setShowAdvanced,
  selectedLineIndex,
  setLineWizardIndex,
  addLine,
  removeCurrentLine,
  updateSelectedBlock,
  updateLine,
  activeLine,
  lineLabel,
  applyConditionAction,
  createId,
  medicationOptions,
  onMedicationQuery,
  onMedicationOptionSelect,
  testGroups,
  DOSE_OPTIONS,
  UNIT_OPTIONS,
  ROUTE_OPTIONS,
  FREQUENCY_OPTIONS,
  MOMENT_OPTIONS,
  MEAL_OPTIONS,
  DURATION_OPTIONS,
  CONDITION_OPTIONS,
  QTY_OPTIONS,
  RENEWAL_OPTIONS,
  CONDITION_ACTION_OPTIONS,
  fieldErrors,
  savedMessage,
  duplicateWarnings,
  extraWarnings = [],
  isSavingEntry,
  onCancelLastBlock,
  cancelDisabled = false,
}) => {
  const handleSelectLine = (idx) => {
    if (!selectedBlock) return;
    setLineWizardIndex((prev) => ({ ...prev, [selectedBlock.id]: idx }));
  };

  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testLineId, setTestLineId] = useState("");

  const removeLineById = (lineId, idx) => {
    if (!selectedBlock || (selectedBlock.lines || []).length <= 1) return;
    const targetLine = (selectedBlock.lines || []).find((l) => l.id === lineId);
    const nextIndex = Math.max(
      0,
      Math.min(idx, (selectedBlock.lines || []).length - 2),
    );

    if (lineId === testLineId) {
      setTestLineId("");
      setTestModalOpen(false);
    }

    updateSelectedBlock((block) => {
      let nextChildren = block.children || [];
      if (
        targetLine?.type === "condition_line" &&
        targetLine.spawned_block_id
      ) {
        nextChildren = nextChildren.filter(
          (child) => child.id !== targetLine.spawned_block_id,
        );
      }
      return {
        ...block,
        lines: (block.lines || []).filter((line) => line.id !== lineId),
        children: nextChildren,
      };
    });

    setLineWizardIndex((prev) => ({ ...prev, [selectedBlock.id]: nextIndex }));
  };

  const lineI18n = {
    dosage_line: { acronym: "POSO", title: "Posologie" },
    condition_line: {
      acronym: "Liaison",
      title: "Prescription conditionnelle",
    },
    note_line: { acronym: "D", title: "Directive" },
    step_line: { acronym: "Puis", title: "Ligne de protocole" },
    test_line: { acronym: "T", title: "Tests" },
  };

  const handleAddTestLine = () => {
    if (!selectedBlock || !isTestMedication) return;
    const newLine = { id: createId("line"), type: "test_line", tests: [] };
    updateSelectedBlock((block) => ({
      ...block,
      lines: [...(block.lines || []), newLine],
    }));
    setLineWizardIndex((prev) => ({
      ...prev,
      [selectedBlock.id]: (selectedBlock.lines || []).length,
    }));
    setTestLineId(newLine.id);
    setTestModalOpen(true);
  };

  const openTestModalForLine = (lineId) => {
    setTestLineId(lineId);
    setTestModalOpen(true);
  };

  const handleTestsSelected = (items) => {
    if (!testLineId) return;
    const tests = items.map((item) => {
      const base = item.value || {};
      return {
        id: base.id || item.id,
        name: base.name || item.name,
        group: base.group || item.group || "",
      };
    });
    updateLine(testLineId, (line) => ({ ...line, tests }));
    setTestModalOpen(false);
    setTestLineId("");
  };

  const handleCloseTestModal = () => {
    setTestModalOpen(false);
    setTestLineId("");
  };

  const testModalSelectedItems = useMemo(() => {
    const targetLine =
      (selectedBlock?.lines || []).find((l) => l.id === testLineId) ||
      (activeLine?.type === "test_line" ? activeLine : null);
    return (
      targetLine?.tests?.map((test) => ({
        id: test.id,
        name: test.name,
        value: test,
      })) || []
    );
  }, [selectedBlock, testLineId, activeLine]);

  const isTestMedication =
    (selectedBlock?.medicament?.name || "").trim().toLowerCase() === "test";

  useEffect(() => {
    if (!isTestMedication || !selectedBlock) return;
    const hasNonTest = (selectedBlock.lines || []).some(
      (l) => l.type !== "test_line",
    );
    if (!hasNonTest) return;
    const newLine = { id: createId("line"), type: "test_line", tests: [] };
    updateSelectedBlock((block) => ({ ...block, lines: [newLine] }));
    setLineWizardIndex((prev) => ({ ...prev, [selectedBlock.id]: 0 }));
    setTestLineId(newLine.id);
    setTestModalOpen(true);
  }, [isTestMedication, selectedBlock]);

  useEffect(() => {
    if (isTestMedication || !selectedBlock) return;
    const hasTestLine = (selectedBlock.lines || []).some(
      (l) => l.type === "test_line",
    );
    if (!hasTestLine) return;
    const nextLines = (selectedBlock.lines || []).filter(
      (l) => l.type !== "test_line",
    );
    const linesFallback = nextLines.length ? nextLines : [createDosageLine()];
    updateSelectedBlock((block) => ({ ...block, lines: linesFallback }));
    setLineWizardIndex((prev) => ({ ...prev, [selectedBlock.id]: 0 }));
    setTestLineId("");
    setTestModalOpen(false);
  }, [isTestMedication, selectedBlock]);

  const getFieldError = (key) => fieldErrors?.[key] || null;
  const getStepLineValue = (line, key) =>
    line?.[key] ?? (line?.steps || [])[0]?.[key] ?? "";
  const fieldClass = (baseClass, key) => {
    const err = getFieldError(key);
    if (!err) return baseClass;
    return `${baseClass} ${err.severity === "warning" ? "np-field-warning" : "np-field-error"}`;
  };

  const getOptionValue = (option) => {
    if (option === null || option === undefined) return "";
    if (typeof option === "string" || typeof option === "number")
      return String(option);
    if (option?.value !== undefined && option?.value !== null)
      return String(option.value);
    if (option?.abbr !== undefined && option?.abbr !== null)
      return String(option.abbr);
    if (option?.label !== undefined && option?.label !== null)
      return String(option.label);
    return "";
  };

  const getOptionLabel = (option) => {
    if (option === null || option === undefined) return "";
    if (typeof option === "string" || typeof option === "number")
      return String(option);
    const label =
      option?.label !== undefined && option?.label !== null
        ? String(option.label)
        : "";
    const abbr =
      option?.abbr !== undefined && option?.abbr !== null
        ? String(option.abbr)
        : "";
    const value =
      option?.value !== undefined && option?.value !== null
        ? String(option.value)
        : "";

    if (label && abbr) return `${label} (${abbr})`;
    if (label) return label;
    if (value) return value;
    if (abbr) return abbr;
    return "";
  };

  const renderListOption = (option, index) => {
    const value = getOptionValue(option);
    if (!value) return null;
    const label = getOptionLabel(option);
    return <option key={`${value}-${index}`} value={value} label={label} />;
  };

  const renderSelectOption = (option, index) => {
    const value = getOptionValue(option);
    if (!value) return null;
    const label = getOptionLabel(option);
    return (
      <option key={`${value}-${index}`} value={value}>
        {label || value}
      </option>
    );
  };

  const normalizeDurationUnit = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "calendrier (du jour 5 au jour 20)") return "calendrier (du jour 5 au jour 20)";
    if (normalized.startsWith("calendrier")) return "calendrier (du jour 5 au jour 20)";
    return String(value || "");
  };

  const isCalendarDuration = (value) =>
    normalizeDurationUnit(value) === "calendrier (du jour 5 au jour 20)";

  const renderDurationOption = (option, index) => {
    const rawValue = getOptionValue(option);
    if (!rawValue) return null;
    const value = normalizeDurationUnit(rawValue);
    const label = getOptionLabel(option);
    return (
      <option key={`${value}-${index}`} value={value}>
        {label || value}
      </option>
    );
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: 4,
          width: "100%",
        }}
      >
        <div
          className="np-card np-form-shell"
          style={{ flex: "1 1 560px", minWidth: "min(440px, 100%)" }}
        >
          <datalist id="np-dose-options">
            {DOSE_OPTIONS.map(renderListOption)}
          </datalist>
          <datalist id="np-unit-options">
            {UNIT_OPTIONS.map(renderListOption)}
          </datalist>
          <datalist id="np-frequency-options">
            {FREQUENCY_OPTIONS.map(renderListOption)}
          </datalist>
          <datalist id="np-route-options">
            {ROUTE_OPTIONS.map(renderListOption)}
          </datalist>
          <datalist id="np-moment-options">
            {MOMENT_OPTIONS.map(renderListOption)}
          </datalist>
          <datalist id="np-meal-options">
            {MEAL_OPTIONS.map(renderListOption)}
          </datalist>
          <datalist id="np-condition-options">
            {CONDITION_OPTIONS.map(renderListOption)}
          </datalist>
          <datalist id="np-qty-type-options">
            {QTY_OPTIONS.map(renderListOption)}
          </datalist>
          <datalist id="np-renewal-options">
            {RENEWAL_OPTIONS.map(renderListOption)}
          </datalist>

          {selectedBlock ? (
            <div className="np-form-header">
              <div className="np-grid np-form-header-grid">
                <FieldWrap error={getFieldError("block.medicament")}>
                  <MedicationSearch
                    options={medicationOptions}
                    value={selectedBlock.medicament?.name || ""}
                    onQueryChange={onMedicationQuery}
                    onChange={(val) => {
                      onMedicationOptionSelect?.(null);
                      updateSelectedBlock((block) => ({
                        ...block,
                        medicament: { ...(block.medicament || {}), name: val },
                      }));
                    }}
                    onSelectOption={(option) => {
                      onMedicationOptionSelect?.(option);
                      updateSelectedBlock((block) => ({
                        ...block,
                        medicament: {
                          ...(block.medicament || {}),
                          name: option.label,
                          shortName: option.value,
                        },
                      }));
                    }}
                  />
                </FieldWrap>
                <FieldWrap error={getFieldError("block.quantite")}>
                  <input
                    type="number"
                    className={fieldClass("np-field", "block.quantite")}
                    placeholder="Quantity"
                    value={selectedBlock.quantite || ""}
                    onChange={(e) =>
                      updateSelectedBlock((block) => ({
                        ...block,
                        quantite: e.target.value,
                      }))
                    }
                  />
                </FieldWrap>
                <FieldWrap error={getFieldError("block.type_quantite")}>
                  <input
                    className={fieldClass("np-field", "block.type_quantite")}
                    list="np-qty-type-options"
                    placeholder="Quantity type"
                    value={selectedBlock.type_quantite || ""}
                    onChange={(e) =>
                      updateSelectedBlock((block) => ({
                        ...block,
                        type_quantite: e.target.value,
                      }))
                    }
                  />
                </FieldWrap>
              </div>
            </div>
          ) : null}

          <div className="np-form-grid">
            <div
              className="np-form-columns"
              style={{ display: "flex", flexDirection: "column", gap: 5 }}
            >
              <div className="np-main">
                {selectedBlock ? (
                  <>
                    <div className="np-wizard">
                      <div
                        className="np-row"
                        style={{
                          marginBottom: 10,
                          position: "relative",
                          flexWrap: "wrap",
                        }}
                      >
                        {(selectedBlock.lines || []).map((line, idx) => (
                          <button
                            key={line.id}
                            className={
                              selectedLineIndex === idx
                                ? "np-wizard-chip active"
                                : "np-wizard-chip"
                            }
                            onClick={() => handleSelectLine(idx)}
                            title={
                              lineI18n[line.type]?.title || lineLabel(line, idx)
                            }
                          >
                            <span>
                              {lineI18n[line.type]?.acronym
                                ? `${lineI18n[line.type].acronym} ${idx + 1}`
                                : lineLabel(line, idx)}
                            </span>
                            {(selectedBlock.lines || []).length > 1 ? (
                              <span
                                className="np-chip-close"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeLineById(line.id, idx);
                                }}
                                aria-label="Remove line"
                              >
                                ×
                              </span>
                            ) : null}
                          </button>
                        ))}
                        {!isTestMedication ? (
                          <>
                            <button
                              className="np-btn-soft"
                              style={{ padding: "6px 10px" }}
                              onClick={() => setAddMenuOpen((v) => !v)}
                            >
                              +
                            </button>
                            {addMenuOpen ? (
                              <div
                                className="np-add-menu"
                                onMouseLeave={() => setAddMenuOpen(false)}
                              >
                                <button
                                  className="np-add-item"
                                  title="Posologie"
                                  onClick={() => {
                                    addLine("dosage_line");
                                    setAddMenuOpen(false);
                                  }}
                                >
                                  POSO
                                </button>
                                <button
                                  className="np-add-item"
                                  title="Ligne de protocole"
                                  onClick={() => {
                                    addLine("step_line");
                                    setAddMenuOpen(false);
                                  }}
                                >
                                  Puis
                                </button>
                                <button
                                  className="np-add-item"
                                  title="Prescription conditionnelle"
                                  onClick={() => {
                                    addLine("condition_line");
                                    setAddMenuOpen(false);
                                  }}
                                >
                                  Liaison
                                </button>
                              </div>
                            ) : null}
                          </>
                        ) : null}
                        {isTestMedication ? (
                          <button
                            className="np-btn"
                            style={{ padding: "6px 10px", marginLeft: 6 }}
                            onClick={handleAddTestLine}
                          >
                            + Tests
                          </button>
                        ) : null}
                      </div>

                      {activeLine && activeLine.type === "dosage_line" && (
                        <>
                          <div className="np-grid">
                            <FieldWrap
                              error={getFieldError(`${activeLine.id}.dose`)}
                            >
                              <input
                                type="number"
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.dose`,
                                )}
                                list="np-dose-options"
                                placeholder="Dose"
                                value={activeLine.dose || ""}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    dose: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(`${activeLine.id}.unite`)}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.unite`,
                                )}
                                list="np-unit-options"
                                placeholder="Unit"
                                value={activeLine.unite || ""}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    unite: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(
                                `${activeLine.id}.frequence`,
                              )}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.frequence`,
                                )}
                                list="np-frequency-options"
                                placeholder="Frequency"
                                value={activeLine.frequence || ""}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    frequence: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(`${activeLine.id}.voie`)}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.voie`,
                                )}
                                list="np-route-options"
                                placeholder="Route"
                                value={activeLine.voie || ""}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    voie: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(`${activeLine.id}.moment`)}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.moment`,
                                )}
                                list="np-moment-options"
                                placeholder="Moment"
                                value={activeLine.moment || ""}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    moment: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(`${activeLine.id}.repas`)}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.repas`,
                                )}
                                list="np-meal-options"
                                placeholder="Meals"
                                value={activeLine.repas || ""}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    repas: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(
                                `${activeLine.id}.duree_unite`,
                              )}
                            >
                              <select
                                className={fieldClass(
                                  "np-select",
                                  `${activeLine.id}.duree_unite`,
                                )}
                                value={activeLine.duree_unite || ""}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    duree_unite: e.target.value,
                                  }))
                                }
                              >
                                <option value="" disabled>
                                  Duration unit
                                </option>
                                {DURATION_OPTIONS.map(renderDurationOption)}
                              </select>
                            </FieldWrap>
                            {isCalendarDuration(activeLine.duree_unite) ? (
                              <>
                                <FieldWrap
                                  error={getFieldError(
                                    `${activeLine.id}.duree_calendar_start`,
                                  )}
                                >
                                  <input
                                    type="number"
                                    className={fieldClass(
                                      "np-field",
                                      `${activeLine.id}.duree_calendar_start`,
                                    )}
                                    placeholder="Start day (e.g. 5)"
                                    value={
                                      activeLine.duree_calendar_start || ""
                                    }
                                    onChange={(e) =>
                                      updateLine(activeLine.id, (line) => ({
                                        ...line,
                                        duree_calendar_start: e.target.value,
                                      }))
                                    }
                                  />
                                </FieldWrap>
                                <FieldWrap
                                  error={getFieldError(
                                    `${activeLine.id}.duree_calendar_end`,
                                  )}
                                >
                                  <input
                                    type="number"
                                    className={fieldClass(
                                      "np-field",
                                      `${activeLine.id}.duree_calendar_end`,
                                    )}
                                    placeholder="End day (e.g. 10)"
                                    value={activeLine.duree_calendar_end || ""}
                                    onChange={(e) =>
                                      updateLine(activeLine.id, (line) => ({
                                        ...line,
                                        duree_calendar_end: e.target.value,
                                      }))
                                    }
                                  />
                                </FieldWrap>
                              </>
                            ) : (
                              <FieldWrap
                                error={getFieldError(
                                  `${activeLine.id}.duree_valeur`,
                                )}
                              >
                                <input
                                  type="number"
                                  className={fieldClass(
                                    "np-field",
                                    `${activeLine.id}.duree_valeur`,
                                  )}
                                  placeholder="Duration value"
                                  value={activeLine.duree_valeur || ""}
                                  onChange={(e) =>
                                    updateLine(activeLine.id, (line) => ({
                                      ...line,
                                      duree_valeur: e.target.value,
                                    }))
                                  }
                                />
                              </FieldWrap>
                            )}
                            <FieldWrap
                              error={getFieldError(
                                `${activeLine.id}.condition`,
                              )}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.condition`,
                                )}
                                list="np-condition-options"
                                placeholder="Condition"
                                value={activeLine.condition || ""}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    condition: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(
                                `${activeLine.id}.renouvellement`,
                              )}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.renouvellement`,
                                )}
                                list="np-renewal-options"
                                placeholder="Renewal"
                                value={activeLine.renouvellement || ""}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    renouvellement: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                          </div>
                        </>
                      )}

                      {activeLine && activeLine.type === "step_line" && (
                        <>
                          <div className="np-grid" style={{ marginBottom: 8 }}>
                            <FieldWrap
                              error={getFieldError(`${activeLine.id}.dose`)}
                            >
                              <input
                                type="number"
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.dose`,
                                )}
                                list="np-dose-options"
                                placeholder="Step dose"
                                value={getStepLineValue(activeLine, "dose")}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    dose: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(`${activeLine.id}.unite`)}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.unite`,
                                )}
                                list="np-unit-options"
                                placeholder="Unit"
                                value={getStepLineValue(activeLine, "unite")}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    unite: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(
                                `${activeLine.id}.frequence`,
                              )}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.frequence`,
                                )}
                                list="np-frequency-options"
                                placeholder="Frequency"
                                value={getStepLineValue(
                                  activeLine,
                                  "frequence",
                                )}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    frequence: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(`${activeLine.id}.voie`)}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.voie`,
                                )}
                                list="np-route-options"
                                placeholder="Route"
                                value={getStepLineValue(activeLine, "voie")}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    voie: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(`${activeLine.id}.moment`)}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.moment`,
                                )}
                                list="np-moment-options"
                                placeholder="Moment"
                                value={getStepLineValue(activeLine, "moment")}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    moment: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(`${activeLine.id}.repas`)}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.repas`,
                                )}
                                list="np-meal-options"
                                placeholder="Meals"
                                value={getStepLineValue(activeLine, "repas")}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    repas: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(
                                `${activeLine.id}.duree_unite`,
                              )}
                            >
                              <select
                                className={fieldClass(
                                  "np-select",
                                  `${activeLine.id}.duree_unite`,
                                )}
                                value={getStepLineValue(
                                  activeLine,
                                  "duree_unite",
                                )}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    duree_unite: e.target.value,
                                  }))
                                }
                              >
                                <option value="" disabled>
                                  Duration unit
                                </option>
                                {DURATION_OPTIONS.map(renderDurationOption)}
                              </select>
                            </FieldWrap>
                            {isCalendarDuration(
                              getStepLineValue(activeLine, "duree_unite"),
                            ) ? (
                              <>
                                <FieldWrap
                                  error={getFieldError(
                                    `${activeLine.id}.duree_calendar_start`,
                                  )}
                                >
                                  <input
                                    type="number"
                                    className={fieldClass(
                                      "np-field",
                                      `${activeLine.id}.duree_calendar_start`,
                                    )}
                                    placeholder="Start day (e.g. 5)"
                                    value={getStepLineValue(
                                      activeLine,
                                      "duree_calendar_start",
                                    )}
                                    onChange={(e) =>
                                      updateLine(activeLine.id, (line) => ({
                                        ...line,
                                        duree_calendar_start: e.target.value,
                                      }))
                                    }
                                  />
                                </FieldWrap>
                                <FieldWrap
                                  error={getFieldError(
                                    `${activeLine.id}.duree_calendar_end`,
                                  )}
                                >
                                  <input
                                    type="number"
                                    className={fieldClass(
                                      "np-field",
                                      `${activeLine.id}.duree_calendar_end`,
                                    )}
                                    placeholder="End day (e.g. 10)"
                                    value={getStepLineValue(
                                      activeLine,
                                      "duree_calendar_end",
                                    )}
                                    onChange={(e) =>
                                      updateLine(activeLine.id, (line) => ({
                                        ...line,
                                        duree_calendar_end: e.target.value,
                                      }))
                                    }
                                  />
                                </FieldWrap>
                              </>
                            ) : (
                              <FieldWrap
                                error={getFieldError(
                                  `${activeLine.id}.duree_valeur`,
                                )}
                              >
                                <input
                                  type="number"
                                  className={fieldClass(
                                    "np-field",
                                    `${activeLine.id}.duree_valeur`,
                                  )}
                                  placeholder="Duration value"
                                  value={getStepLineValue(
                                    activeLine,
                                    "duree_valeur",
                                  )}
                                  onChange={(e) =>
                                    updateLine(activeLine.id, (line) => ({
                                      ...line,
                                      duree_valeur: e.target.value,
                                    }))
                                  }
                                />
                              </FieldWrap>
                            )}
                            <FieldWrap
                              error={getFieldError(
                                `${activeLine.id}.condition`,
                              )}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.condition`,
                                )}
                                list="np-condition-options"
                                placeholder="Condition"
                                value={getStepLineValue(
                                  activeLine,
                                  "condition",
                                )}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    condition: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(
                                `${activeLine.id}.renouvellement`,
                              )}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.renouvellement`,
                                )}
                                list="np-renewal-options"
                                placeholder="Renewal"
                                value={getStepLineValue(
                                  activeLine,
                                  "renouvellement",
                                )}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    renouvellement: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                          </div>
                        </>
                      )}

                      {activeLine && activeLine.type === "condition_line" && (
                        <>
                          <div className="np-grid" style={{ marginBottom: 8 }}>
                            <FieldWrap
                              error={getFieldError(
                                `${activeLine.id}.expression`,
                              )}
                            >
                              <input
                                className={fieldClass(
                                  "np-field",
                                  `${activeLine.id}.expression`,
                                )}
                                list="np-condition-options"
                                placeholder="Condition"
                                value={activeLine.expression}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    expression: e.target.value,
                                  }))
                                }
                              />
                            </FieldWrap>
                            <FieldWrap
                              error={getFieldError(
                                `${activeLine.id}.action_type`,
                              )}
                            >
                              <select
                                className={fieldClass(
                                  "np-select",
                                  `${activeLine.id}.action_type`,
                                )}
                                value={activeLine.action_type}
                                onChange={(e) =>
                                  updateLine(activeLine.id, (line) => ({
                                    ...line,
                                    action_type: e.target.value,
                                  }))
                                }
                              >
                                {CONDITION_ACTION_OPTIONS.map((o) => (
                                  <option key={o}>{o}</option>
                                ))}
                              </select>
                            </FieldWrap>
                          </div>

                          <div className="np-row">
                            <button
                              className="np-btn"
                              onClick={applyConditionAction}
                            >
                              valider la liaison
                            </button>
                            {showAdvanced && activeLine.spawned_block_id ? (
                              <span className="np-label np-advanced-label">
                                Linked block created
                              </span>
                            ) : null}
                          </div>
                        </>
                      )}

                      {activeLine && activeLine.type === "test_line" && (
                        <div
                          className="np-card"
                          style={{ padding: 12, marginTop: 4 }}
                        >
                          <div
                            className="np-row"
                            style={{
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <div
                                className="np-panel-title"
                                style={{ marginBottom: 4 }}
                              >
                                Tests selection
                              </div>
                              <div className="np-label">
                                Saved as one entry; each test prints on its own
                                line.
                              </div>
                            </div>
                            <div className="np-field-wrap">
                              <button
                                className={fieldClass(
                                  "np-btn",
                                  `${activeLine.id}.tests`,
                                )}
                                onClick={() =>
                                  openTestModalForLine(activeLine.id)
                                }
                              >
                                Select tests
                              </button>
                              <FieldIndicator
                                error={getFieldError(`${activeLine.id}.tests`)}
                              />
                            </div>
                          </div>
                          {activeLine.tests && activeLine.tests.length ? (
                            <div style={{ marginTop: 8, paddingLeft: 4 }}>
                              {activeLine.tests.map((test) => (
                                <div
                                  key={test.id}
                                  className="np-text-soft"
                                >{`- ${test.name}${test.group ? ` (${test.group})` : ""}`}</div>
                              ))}
                            </div>
                          ) : (
                            <div
                              className="np-text-soft"
                              style={{ marginTop: 8 }}
                            >
                              No tests selected yet.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {savedMessage ? (
                      <div className="np-success">{savedMessage}</div>
                    ) : null}
                    {[...(duplicateWarnings || []), ...(extraWarnings || [])]
                      .filter(Boolean)
                      .length > 0 ? (
                      <div className="np-warning">
                        {[
                          ...(duplicateWarnings || []),
                          ...(extraWarnings || []),
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="np-warning">No block selected.</div>
                )}
              </div>

              <div
                className="np-side"
                style={{ padding: "0px", border: "none" }}
              >
                <div className="np-entry-wide">
                  <textarea
                    className="np-textarea"
                    placeholder="Entry note"
                    value={selectedEntry?.note || ""}
                    onChange={(e) =>
                      onUpdateEntry((entry) => ({
                        ...entry,
                        note: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="np-preview"
          style={{ flex: "1 1 320px", minWidth: "min(300px, 100%)", marginTop: 0 }}
        >
          <div>
            <h4 className="np-section-title">Visual preview</h4>
            <div className="np-preview-list">
              <PreviewBlocks
                blocks={blockTree}
                entryNote={previewEntryNote}
                selectedBlockId={selectedBlockId}
                selectedLineId={selectedLineId}
                onSelectLine={onSelectPreviewLine}
                onSelectBlock={onSelectPreviewBlock}
                onDeleteLine={onDeletePreviewLine}
                showShortName={true}
                allowUntitled={true}
              />
            </div>
          </div>
          <div
            className="np-row"
            style={{ gap: 6, marginTop: 10, justifyContent: "flex-end" }}
          >
            {typeof onCancelLastBlock === "function" && (
              <button
                className="np-btn-cancel-last"
                onClick={onCancelLastBlock}
                disabled={cancelDisabled}
                title="Cancel the last added medication"
              >
                Cancel
              </button>
            )}
            <button
              className="np-btn-soft"
              onClick={onResetEntryForm}
              disabled={!selectedEntry || isSavingEntry}
            >
              Reset form
            </button>
            <button
              className="np-btn"
              onClick={onSaveEntry}
              disabled={isSavingEntry}
            >
              {isSavingEntry ? "Saving..." : "Save entry"}
            </button>
          </div>
        </div>
      </div>

      <GroupedSelectionModal
        show={testModalOpen}
        onClose={handleCloseTestModal}
        title="Select tests"
        groups={testGroups}
        onSelect={handleTestsSelected}
        savedSelections={[]}
        selectedItems={testModalSelectedItems}
      />
    </>
  );
};

export default PrescriptionForm;
